import { salutationFor } from "@signalist/shared-types";
import {
	Dispatch,
	FunctionComponent,
	MouseEvent as ReactMouseEvent,
	SetStateAction,
	useEffect,
	useRef,
	useState,
} from "react";

import ReactDOM from "react-dom";

import {
	faCheckCircle,
	faCircle,
	faClock,
	faLock,
	faPlus,
	faSquare,
	faSquareCheck,
	faStar,
	faWarning,
} from "@fortawesome/pro-regular-svg-icons";

import { CircularProgress } from "@mui/material";

import { format } from "date-fns";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { CategorySegmentCell } from "@/components/CategoryReasoningPopover/CategorySegmentCell";

import { COMPANY_ONLY_SCORE_NOTE } from "@/components/LeadScoringPopover/LeadScoringPopover";
import { renderCurrentScoreResult } from "@/pages/Private/pages/List/utils/renderCurrentScoreResult";
import { Dropdown } from "@/components/Dropdown/Dropdown";
import { TextArea } from "@/components/TextArea/TextArea";
import { TableColumn } from "@/components/Table/Table";
import { SortParams } from "@/types/types";
import { classNames } from "@/utils/classNames";
import { ensureHttpProtocol } from "@/utils/url";
import { useUpdateContactProspectMutation } from "@/pages/Private/redux/contact/contact.api";
import { updateContactProspectGender } from "@/pages/Private/redux/contact/contact.slice";
import { useAppDispatch } from "@/redux/hooks";
import { useUpdateOrganizationScoringMutation } from "@/pages/Private/redux/organization/organization.api";
import { useGetScoreQuery } from "@/pages/Private/redux/score/score.api";

import { AssignStatus, ListSourceType } from "@/enum/list.enum";
import { ProspectInterest } from "@/enum/prospect.enum";

import { getGenderConfidenceState } from "@/utils/genderConfidence";

import { useAnchoredTooltip } from "@/utils/useAnchoredTooltip";

import { useCloseOnOutsideClick } from "@/utils/useCloseOnOutsideClick";

import { CompanyCategoryResults } from "../../Apollo/schema/get-people";

import {
	ContactProspect,
	ContactCompetitorOrganization,
	OrganizationInfo,
	OrganizationProspectInfo,
} from "../schema/contacts";
import {
	getLastListPerson,
	getOriginList,
	getSentimentColor,
	formatSentimentLabel,
} from "./listPersonHelpers";
import { getOrderStatus } from "./column-utils";
import { RenderContactOverallScore } from "./RenderContactOverallScore";
import {
	renderContactCompanyFoundedYearColumn,
	renderContactCompanyIndustryColumn,
	renderContactCompanySizeColumn,
	renderContactNormalizedCompanyNameColumn,
} from "./columns/contact-company-facts-columns";
import { EditableContactNameColumn } from "../components/EditableContactNameColumn";
import { EditableContactCompanyColumn } from "../components/EditableContactCompanyColumn";
import { EditableOrganizationNameColumn } from "../components/EditableOrganizationNameColumn";
import { convertHtmlToLinkedInText } from "../../List/components/CampaignOverview/components/Lead/utils/convertHtmlToLinkedin.util";

// ─── Select column ───────────────────────────────────────────────────────────

export const renderContactSelectColumn = (
	setShowAll: Dispatch<SetStateAction<boolean>>,
	toggleSelectAll: () => void,
	selectAll: boolean,
	selectedItems: number[],
	handleSelectItem: (id: number) => void
): TableColumn => {
	return {
		title: "",
		uid: "contactSelect",
		icon: selectAll ? faSquareCheck : faSquare,
		allowExclude: false,
		calcWidth: 62.5,
		locked: "leftFirst",
		hideable: false,
		moveable: false,
		show: true,
		onHeaderClick: () => {
			if (selectAll) {
				toggleSelectAll();
			} else {
				setShowAll(true);
			}
		},
		render: (row: ContactProspect, index?: number) => (
			<div className="group flex items-center">
				<div
					className={classNames(
						"w-[16px] text-center font-medium text-table-header",
						selectedItems?.find((lf) => lf === row.id) ? "hidden" : "group-hover:hidden"
					)}
				>
					{typeof index !== "undefined" ? index + 1 : ""}
				</div>
				<Checkbox
					className={classNames(
						selectedItems?.find((lf) => lf === row.id) ? "block" : "hidden group-hover:block"
					)}
					isChecked={!!selectedItems?.find((lf) => lf === row.id)}
					name={"selectedContacts"}
					onChange={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();

						handleSelectItem(row.id);
					}}
				/>
			</div>
		),
	};
};

// ─── Prospect columns (Contacts tab) ────────────────────────────────────────

export const renderProspectNameColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	setShowEdit: (val: { id: number; name: string }) => void,
	showEdit: { id: number; name: string } | undefined
): TableColumn => {
	const orderTitle = "prospect.name";

	return {
		title: ts("table.name"),
		width: "fit-content",
		minWidth: "260px",
		calcWidth: 260,
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "prospectName",
		hideable: true,
		moveable: false,
		locked: "leftFourth",
		icon: faLock,
		show: true,
		render: (row: ContactProspect) => (
			<EditableContactNameColumn
				handleCopyClick={handleCopyClick}
				orderTitle={orderTitle}
				row={row}
				setShowEdit={setShowEdit}
				showEdit={showEdit}
			/>
		),
	};
};

export const renderProspectEmailColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.email";

	return {
		title: ts("table.email"),
		width: "fit-content",
		minWidth: "200px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "prospectEmail",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => (
			<button
				className="text-sm text-gray-700 truncate cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.email) {
						handleCopyClick(row.email);
					}
				}}
			>
				{row.email || "-"}
			</button>
		),
	};
};

export const getCompanyScoreBadge = (companyResult: CompanyCategoryResults | null | undefined) => {
	if (!companyResult) {
		return null;
	}

	const { leadScore, leadScoringReasoning, result, reason, companyCategory } = companyResult;

	const isCompanyOnlyScore = ["1", "2", "3"].includes(leadScore ?? "");

	let score: string | null = null;

	if (isCompanyOnlyScore) {
		score = `C${leadScore}`;
	} else if (result === "Unsure") {
		score = "?";
	} else if (
		result === "Competitor" ||
		result === "Disqualified" ||
		companyCategory === "Disqualified"
	) {
		score = "DQ";
	}

	if (!score) {
		return null;
	}

	return renderCurrentScoreResult({
		scoring: {
			leadScoringBreakdown: isCompanyOnlyScore ? companyResult.leadScoringBreakdown : null,
			overallReasoning: isCompanyOnlyScore ? leadScoringReasoning || reason : reason,
		},
		note: isCompanyOnlyScore ? COMPANY_ONLY_SCORE_NOTE : undefined,
		score,
	});
};

export const renderProspectCompanyColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	setShowEdit: (val: { id: number; name: string }) => void,
	showEdit: { id: number; name: string } | undefined
): TableColumn => {
	const orderTitle = "organization.name";

	return {
		title: ts("table.company"),
		width: "fit-content",
		minWidth: "270px",
		calcWidth: 270,
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "prospectCompany",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => {
			const scoreBadge = getCompanyScoreBadge(row.organization?.scoreCompanyCategoryResult);

			return (
				<div className="flex items-center gap-1.5">
					{scoreBadge}
					<EditableContactCompanyColumn
						handleCopyClick={handleCopyClick}
						orderTitle={orderTitle}
						row={row}
						setShowEdit={setShowEdit}
						showEdit={showEdit}
					/>
				</div>
			);
		},
	};
};

export const renderProspectSeniorityColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.seniority";

	return {
		title: ts("table.seniority"),
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "prospectSeniority",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => (
			<span className="text-sm text-gray-700">{row.seniority || "-"}</span>
		),
	};
};

export const renderProspectLocationColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.country";

	return {
		title: ts("table.location"),
		width: "fit-content",
		minWidth: "140px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "prospectLocation",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => {
			const location = [row.city, row.country].filter(Boolean).join(", ") || "-";

			return (
				<button
					className="text-sm text-gray-700 truncate cursor-pointer"
					type="button"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						if (location !== "-") {
							handleCopyClick(location);
						}
					}}
				>
					{location}
				</button>
			);
		},
	};
};

export const renderProspectLinkedinColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	return {
		title: ts("table.linkedin"),
		width: "fit-content",
		minWidth: "80px",
		uid: "prospectLinkedin",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) =>
			row.linkedinUrl ? (
				<a
					className="text-sm text-blue-action hover:underline"
					href={ensureHttpProtocol(row.linkedinUrl)}
					rel="noopener noreferrer"
					target="_blank"
					onClick={(e) => e.stopPropagation()}
				>
					View
				</a>
			) : (
				<span className="text-sm text-gray-400">-</span>
			),
	};
};

export const renderContactJobTitleColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.title";

	return {
		title: ts("table.jobTitle"),
		width: "fit-content",
		minWidth: "150px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactJobTitle",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => (
			<button
				className="text-sm text-gray-700 text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.title) {
						handleCopyClick(row.title);
					}
				}}
			>
				{row.title || "-"}
			</button>
		),
	};
};

export const renderContactPersonCountryColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.country";

	return {
		title: ts("table.personalCountry"),
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactPersonCountry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.country) {
						handleCopyClick(row.country);
					}
				}}
			>
				{row.country || "-"}
			</button>
		),
	};
};

export const renderContactCompanyCountryColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.country";

	return {
		title: ts("table.companyCountry"),
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactCompanyCountry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.organization?.country) {
						handleCopyClick(row.organization.country);
					}
				}}
			>
				{row.organization?.country || "-"}
			</button>
		),
	};
};

// ─── Gender + Salutation columns ────────────────────────────────────────────

const GENDER_OPTIONS = [
	{ id: "male", title: "Male" },
	{ id: "female", title: "Female" },
	{ id: "", title: "— Clear —" },
];

const GENDER_CONFIRM_OPTION_ID = "__confirm__";

const EditableContactGenderColumn: FunctionComponent<{
	row: ContactProspect;
	handleCopyClick: (value: string) => void;
	setShowEdit: (val: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	orderTitle: string;
	ts: (key: string) => string;
}> = ({ row, handleCopyClick, orderTitle, setShowEdit, showEdit, ts }) => {
	const dispatch = useAppDispatch();
	const [updateProspect, { isLoading }] = useUpdateContactProspectMutation();

	if (isLoading) {
		return <CircularProgress size={16} />;
	}

	const gender = row.gender;
	const genderProbability = row.genderProbability;
	const confidence = getGenderConfidenceState(gender, genderProbability);
	const isEditing = showEdit && showEdit.id === row.id && showEdit.name === orderTitle;

	const dropdownOptions =
		confidence === "unsure" && gender
			? [
					{ id: GENDER_CONFIRM_OPTION_ID, title: ts("table.genderConfirmOption") },
					...GENDER_OPTIONS,
				]
			: GENDER_OPTIONS;

	const handleDropdownSelect = async (selected: { id: number | string | null }) => {
		const nextGender =
			selected.id === GENDER_CONFIRM_OPTION_ID ? (gender ?? "") : (selected.id as string);

		await updateProspect({
			id: row.id,
			gender: nextGender,
		}).unwrap();

		dispatch(
			updateContactProspectGender({
				prospectId: row.id,
				gender: nextGender === "" ? null : nextGender,
			})
		);

		setShowEdit({ id: 0, name: "" });
	};

	const renderIndicator = () => {
		if (confidence === "confirmed") {
			return (
				<Tooltip content={ts("table.genderConfirmedTooltip")}>
					<Icon className="text-status-success h-4 w-4" icon={faCheckCircle} />
				</Tooltip>
			);
		}

		if (confidence === "unsure") {
			return (
				<Tooltip content={ts("table.genderUnsureTooltip")}>
					<button
						className="cursor-pointer flex items-center"
						type="button"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							setShowEdit({ id: row.id, name: orderTitle });
						}}
					>
						<Icon className="text-status-warning h-4 w-4" icon={faWarning} />
					</button>
				</Tooltip>
			);
		}

		if (confidence === "empty") {
			return (
				<Tooltip content={ts("table.genderSetTooltip")}>
					<button
						className="cursor-pointer flex items-center"
						type="button"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							setShowEdit({ id: row.id, name: orderTitle });
						}}
					>
						<Icon className="text-text-meta h-4 w-4" icon={faPlus} />
					</button>
				</Tooltip>
			);
		}

		return null;
	};

	return (
		<div className="relative flex w-full min-w-[100px] max-w-[250px] flex-row justify-between items-center gap-1">
			{isEditing ? (
				<div className="min-w-[80px] absolute z-10">
					<Dropdown
						data={dropdownOptions}
						defaultValue={gender ? GENDER_OPTIONS.find((o) => o.id === gender) : undefined}
						floating={true}
						handleSelect={handleDropdownSelect}
						portal={true}
						size={ButtonSize.XS}
					/>
				</div>
			) : (
				<>
					<button
						className="text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							handleCopyClick(gender ?? "");
						}}
						onContextMenu={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setShowEdit({ id: row.id, name: orderTitle });
						}}
					>
						{gender?.capitalizeString() || "-"}
					</button>
					{renderIndicator()}
				</>
			)}
		</div>
	);
};

export const renderContactGenderColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	setShowEdit: (val: { id: number; name: string }) => void,
	showEdit: { id: number; name: string } | undefined
): TableColumn => {
	const orderTitle = "prospect.gender";

	return {
		title: ts("table.gender"),
		width: "fit-content",
		minWidth: "100px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactGender",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => (
			<EditableContactGenderColumn
				handleCopyClick={handleCopyClick}
				orderTitle={orderTitle}
				row={row}
				setShowEdit={setShowEdit}
				showEdit={showEdit}
				ts={ts}
			/>
		),
	};
};

export const renderContactSalutationDeColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.salutationDe"),
		width: "fit-content",
		minWidth: "80px",
		uid: "contactSalutationDe",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => {
			return <span className="text-sm text-gray-700">{salutationFor(row, "de") || "-"}</span>;
		},
	};
};

export const renderContactSalutationEnColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.salutationEn"),
		width: "fit-content",
		minWidth: "80px",
		uid: "contactSalutationEn",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => {
			return <span className="text-sm text-gray-700">{salutationFor(row, "en") || "-"}</span>;
		},
	};
};

// ─── Scoring & Category columns ──────────────────────────────────────────────

export const renderContactLeadScoringColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "score_persona_category_result.overallScore";

	return {
		title: "",
		width: "fit-content",
		calcWidth: 54,
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactLeadScoring",
		locked: "leftSecond",
		hideable: false,
		moveable: false,
		show: true,
		render: (row: ContactProspect) => {
			const scoring = row.scorePersonaCategoryResult;
			const companyCategory = row.organization?.scoreCompanyCategoryResult;

			if (!scoring && !companyCategory) {
				return <Icon className="h-[6px] opacity-[0.5]" color="#9CA3AF" icon={faCircle} />;
			}

			return (
				<RenderContactOverallScore
					companyCategory={companyCategory}
					contactProspect={row}
					scoring={scoring}
				/>
			);
		},
	};
};

export const renderContactCompanyCategoryColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "score_company_category_result.result";

	return {
		title: ts("table.companyCategory"),
		iconRender: () => {
			return <Icon className="ml-1 text-action-400" icon={faStar} />;
		},
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		allowExclude: true,
		uid: "contactCompanyCategory",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => (
			<CategorySegmentCell
				categoryName={row.organization?.scoreCompanyCategoryResult?.scoreCompanyCategory?.category}
				kind="company"
				prioScore={row.scorePersonaCategoryResult?.companyScore}
				reasoning={row.organization?.scoreCompanyCategoryResult?.reason}
			/>
		),
	};
};

export const renderContactPersonaCategoryColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "score_persona_category_result.result";

	return {
		title: ts("table.personaCategory"),
		iconRender: () => {
			return <Icon className="ml-1 text-action-400" icon={faStar} />;
		},
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		allowExclude: true,
		uid: "contactPersonaCategory",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => (
			<CategorySegmentCell
				categoryName={row.scorePersonaCategoryResult?.scorePersonaCategory?.category}
				kind="persona"
				prioScore={row.scorePersonaCategoryResult?.personaScore}
				reasoning={row.scorePersonaCategoryResult?.reason}
			/>
		),
	};
};

// ─── Organization columns (Companies tab) ───────────────────────────────────

export const renderOrgNameColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	setShowEdit: (val: { id: number; name: string }) => void,
	showEdit: { id: number; name: string } | undefined
): TableColumn => {
	const orderTitle = "organization.name";

	return {
		title: ts("table.company"),
		width: "fit-content",
		minWidth: "280px",
		calcWidth: 280,
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "orgName",
		hideable: true,
		locked: "contactsOrgLeftFirst",
		icon: faLock,
		moveable: false,
		show: true,
		render: (row: OrganizationInfo) => (
			<EditableOrganizationNameColumn
				handleCopyClick={handleCopyClick}
				orderTitle={orderTitle}
				row={row}
				setShowEdit={setShowEdit}
				showEdit={showEdit}
			/>
		),
	};
};

export const renderOrgIndustryColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.industry";

	return {
		title: ts("table.industry"),
		width: "fit-content",
		minWidth: "130px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "orgIndustry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: OrganizationInfo) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.industry) {
						handleCopyClick(row.industry);
					}
				}}
			>
				{row.industry || "-"}
			</button>
		),
	};
};

export const renderOrgEmployeesColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.estimatedNumEmployees";

	return {
		title: ts("table.employees"),
		width: "fit-content",
		minWidth: "110px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "orgEmployees",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: OrganizationInfo) => {
			const getEmployeeDisplay = (): string => {
				if (row.employeesFrom != null && row.employeesTo != null) {
					return `${row.employeesFrom.toLocaleString()} - ${row.employeesTo.toLocaleString()}`;
				}

				if (row.employeesFrom != null) {
					return `${row.employeesFrom.toLocaleString()}+`;
				}

				if (row.employeesTo != null) {
					return `up to ${row.employeesTo.toLocaleString()}`;
				}

				return row.estimatedNumEmployees?.toLocaleString() || "-";
			};

			const display = getEmployeeDisplay();

			return (
				<button
					className="text-sm text-gray-700 cursor-pointer"
					type="button"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						if (display !== "-") {
							handleCopyClick(display);
						}
					}}
				>
					{display}
				</button>
			);
		},
	};
};

export const renderOrgCityColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.city";

	return {
		title: ts("table.companyCity"),
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "orgCity",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: OrganizationInfo) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.city) {
						handleCopyClick(row.city);
					}
				}}
			>
				{row.city || "-"}
			</button>
		),
	};
};

export const renderOrgStateColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.state";

	return {
		title: ts("table.companyState"),
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "orgState",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: OrganizationInfo) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.state) {
						handleCopyClick(row.state);
					}
				}}
			>
				{row.state || "-"}
			</button>
		),
	};
};

export const renderOrgFoundedYearColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.foundedYear";

	return {
		title: ts("table.founded"),
		width: "fit-content",
		minWidth: "90px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "orgFoundedYear",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: OrganizationInfo) => (
			<span className="text-sm text-gray-700">{row.foundedYear || "-"}</span>
		),
	};
};

export const renderOrgWebsiteColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.website"),
		width: "fit-content",
		minWidth: "90px",
		uid: "orgWebsite",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: OrganizationInfo) =>
			row.websiteUrl ? (
				<a
					className="text-sm text-blue-action hover:underline"
					href={ensureHttpProtocol(row.websiteUrl)}
					rel="noopener noreferrer"
					target="_blank"
					onClick={(e) => e.stopPropagation()}
				>
					Visit
				</a>
			) : (
				<span className="text-sm text-gray-400">-</span>
			),
	};
};

// ─── Organization select column ─────────────────────────────────────────────

export const renderOrgSelectColumn = (
	setShowAll: Dispatch<SetStateAction<boolean>>,
	toggleSelectAll: () => void,
	selectAll: boolean,
	selectedItems: number[],
	handleSelectItem: (id: number) => void
): TableColumn => {
	return {
		title: "",
		uid: "orgSelect",
		icon: selectAll ? faSquareCheck : faSquare,
		allowExclude: false,
		calcWidth: 62.5,
		locked: "leftFirst",
		hideable: false,
		moveable: false,
		show: true,
		onHeaderClick: () => {
			if (selectAll) {
				toggleSelectAll();
			} else {
				setShowAll(true);
			}
		},
		render: (row: OrganizationInfo, index?: number) => (
			<div className="group flex items-center">
				<div
					className={classNames(
						"w-[16px] text-center font-medium text-table-header",
						selectedItems?.find((lf) => lf === row.id) ? "hidden" : "group-hover:hidden"
					)}
				>
					{typeof index !== "undefined" ? index + 1 : ""}
				</div>
				<Checkbox
					className={classNames(
						selectedItems?.find((lf) => lf === row.id) ? "block" : "hidden group-hover:block"
					)}
					isChecked={!!selectedItems?.find((lf) => lf === row.id)}
					name={"selectedOrganizations"}
					onChange={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();

						handleSelectItem(row.id);
					}}
				/>
			</div>
		),
	};
};

// ─── Prospect avatar helpers ──────────────────────────────────────────────────

const ProspectAvatar: FunctionComponent<{ prospect: OrganizationProspectInfo }> = ({
	prospect,
}) => {
	const [imageError, setImageError] = useState(false);
	const firstName = prospect.firstName || "";
	const lastName = prospect.lastName || "";
	const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
	const fullName = [firstName, lastName].filter(Boolean).join(" ") || "Unknown";

	const tooltipContent = (
		<div className="flex flex-col gap-1">
			<span className="font-semibold">{fullName}</span>
			{prospect.title && <span className="text-xs text-text-meta">{prospect.title}</span>}
		</div>
	);

	return (
		<Tooltip content={tooltipContent}>
			<div className="relative">
				{prospect.image && !imageError ? (
					<img
						alt={fullName}
						className="w-7 h-7 rounded-full border-2 border-white object-cover"
						referrerPolicy="no-referrer"
						src={prospect.image}
						onError={() => setImageError(true)}
					/>
				) : (
					<div className="flex items-center justify-center w-7 h-7 rounded-full text-white text-[10px] font-medium border-2 border-white bg-blue-action">
						{initials || "?"}
					</div>
				)}
			</div>
		</Tooltip>
	);
};

export const renderOrgProspectCountColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	return {
		title: ts("table.prospects"),
		width: "fit-content",
		minWidth: "150px",
		uid: "orgProspectCount",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: OrganizationInfo) => {
			const prospects = row.prospects ?? [];

			if (prospects.length === 0) {
				return <span className="text-gray-400">-</span>;
			}

			const maxVisible = 4;
			const visible = prospects.slice(0, maxVisible);
			const remaining = prospects.length - maxVisible;

			return (
				<div className="flex items-center -space-x-2">
					{visible.map((prospect) => (
						<ProspectAvatar key={prospect.id} prospect={prospect} />
					))}
					{remaining > 0 && (
						<Tooltip
							content={
								<div className="flex flex-col gap-1">
									{prospects.slice(maxVisible).map((p) => (
										<span key={p.id} className="text-xs">
											{[p.firstName, p.lastName].filter(Boolean).join(" ") || "Unknown"}
										</span>
									))}
								</div>
							}
						>
							<div className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-600 text-[10px] font-medium border-2 border-white z-10">
								+{remaining}
							</div>
						</Tooltip>
					)}
				</div>
			);
		},
	};
};

export const renderOrgCompanyCategoryColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "score_company_category_result.result";

	return {
		title: ts("table.companyCategory"),
		iconRender: () => {
			return <Icon className="ml-1 text-action-400" icon={faStar} />;
		},
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		allowExclude: true,
		uid: "orgCompanyCategory",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: OrganizationInfo) => (
			<CategorySegmentCell
				categoryName={
					row.scoreCompanyCategoryResult?.scoreCompanyCategory?.category ||
					row.scoreCompanyCategoryResult?.companyCategory
				}
				kind="company"
				prioScore={row.scoreCompanyCategoryResult?.leadScore}
				reasoning={row.scoreCompanyCategoryResult?.reason}
			/>
		),
	};
};

// ─── Organization scoring ───────────────────────────────────────────────────

export const RenderOrganizationOverallScore: FunctionComponent<{
	organization: OrganizationInfo;
}> = ({ organization }) => {
	const { data: score } = useGetScoreQuery();
	const companyResult = organization.scoreCompanyCategoryResult;

	const [selectedCompanyCategory, setSelectedCompanyCategory] = useState<{
		id: number;
		title: string;
	} | null>(null);

	useEffect(() => {
		if (companyResult?.scoreCompanyCategory || companyResult?.companyCategory) {
			setSelectedCompanyCategory({
				id: companyResult?.scoreCompanyCategory?.id ?? 0,
				title:
					companyResult?.scoreCompanyCategory?.category || companyResult?.companyCategory || "",
			});
		}
	}, [organization]);

	const [isVisible, setIsVisible] = useState(false);
	const [reason, setReason] = useState("");
	const [updateOrganizationScoring, { isLoading }] = useUpdateOrganizationScoringMutation();

	const tooltipRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();

		await updateOrganizationScoring({
			organizationId: organization.id,
			scoreCompanyCategoryId: selectedCompanyCategory?.id || 0,
			companyResult: selectedCompanyCategory?.title || "",
			reason,
		}).unwrap();

		setIsVisible(false);
		setReason("");
	};

	useCloseOnOutsideClick(tooltipRef, isVisible, setIsVisible);

	useAnchoredTooltip(triggerRef, tooltipRef, isVisible);

	const renderChangeResult = () => {
		return ReactDOM.createPortal(
			<div
				ref={tooltipRef}
				className="fixed flex flex-col z-100 p-4 gap-4 bg-white rounded-lg shadow-lg z-[199] min-w-[300px]"
				onClick={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<div className="flex flex-col gap-2">
					<div>
						<Dropdown
							data={[
								...(score?.prospectCompanyCategories?.map((category) => ({
									id: category.id || 0,
									title: `${category.category} (${category.type?.capitalizeString() ?? ""})`,
								})) || []),
								...(score?.partnerCompanyCategories?.map((category) => ({
									id: category.id || 0,
									title: `${category.category} (${category.type?.capitalizeString() ?? ""})`,
								})) || []),
								{
									id: 0,
									title: "Disqualified",
								},
								{
									id: 0,
									title: "Competitor",
								},
							]}
							defaultValue={
								selectedCompanyCategory
									? {
											id: selectedCompanyCategory.id,
											title: selectedCompanyCategory.title,
										}
									: undefined
							}
							portal={true}
							handleSelect={(selected) => {
								setSelectedCompanyCategory({
									id: selected.id as number,
									title: selected.title,
								});
							}}
							label="Company category"
						/>
					</div>
				</div>
				<div>
					<TextArea
						handleChange={(e) => setReason(e.target.value)}
						label="Reason for change"
						name="reason"
						placeholder="Enter reason for this change..."
						showError={false}
						value={reason}
					/>
				</div>
				<div className="flex flex-row gap-2 justify-end">
					<Button
						color={ButtonColor.ACTION}
						title="Save"
						disabled={isLoading}
						onClick={handleSave}
					/>
				</div>
			</div>,
			document.body
		);
	};

	const leadScore = companyResult?.leadScore;
	const result = companyResult?.result;
	const companyReason = companyResult?.reason;

	if (companyResult && ["1", "2", "3"].includes(leadScore ?? "")) {
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					scoring: {
						leadScoringBreakdown: companyResult.leadScoringBreakdown,
						overallReasoning: companyResult.leadScoringReasoning,
					},
					note: COMPANY_ONLY_SCORE_NOTE,
					score: `C${leadScore}`,
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if (result === "Unsure") {
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					scoring: { overallReasoning: companyReason },
					score: "?",
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if (
		result === "Competitor" ||
		result === "Disqualified" ||
		companyResult?.companyCategory === "Disqualified"
	) {
		return (
			<>
				{isVisible && renderChangeResult()}
				{renderCurrentScoreResult({
					scoring: { overallReasoning: companyReason },
					score: "DQ",
					triggerRef,
					setIsVisible,
					isVisible,
				})}
			</>
		);
	} else if (companyResult) {
		return (
			<>
				{isVisible && renderChangeResult()}
				<Tooltip content={"Still ongoing"}>
					<div
						ref={triggerRef}
						className="cursor-pointer w-[24px] h-[24px] text-text-meta text-xxs font-bold bg-border-light rounded-full justify-center items-center flex"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							setIsVisible(!isVisible);
						}}
					>
						<Icon className="text-text-disabled w-6 h-6 p-1" icon={faClock} />
					</div>
				</Tooltip>
			</>
		);
	}

	return <></>;
};

export const renderOrgLeadScoringColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "score_company_category_result.leadScore";

	return {
		title: "",
		width: "fit-content",
		calcWidth: 54,
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "orgLeadScoring",
		locked: "leftSecond",
		hideable: false,
		moveable: false,
		show: true,
		render: (row: OrganizationInfo) => {
			const companyResult = row.scoreCompanyCategoryResult;

			if (!companyResult) {
				return <Icon className="h-[6px] opacity-[0.5]" color="#9CA3AF" icon={faCircle} />;
			}

			return <RenderOrganizationOverallScore organization={row} />;
		},
	};
};

export const renderOrgCountryColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.country";

	return {
		title: ts("table.companyCountry"),
		width: "fit-content",
		minWidth: "130px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "orgCountry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: OrganizationInfo) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.country) {
						handleCopyClick(row.country);
					}
				}}
			>
				{row.country || "-"}
			</button>
		),
	};
};

// ─── Competitor columns ─────────────────────────────────────────────────────

export const renderCompetitorNameColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.name";

	return {
		title: ts("table.company"),
		width: "fit-content",
		minWidth: "200px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "competitorName",
		hideable: true,
		moveable: false,
		show: true,
		render: (row: ContactCompetitorOrganization) => (
			<div className="flex items-center gap-2">
				<div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-xs font-medium text-red-600">
					{row.organization.name?.[0]?.toUpperCase()}
				</div>
				<div className="flex flex-col">
					<button
						className="text-sm font-medium text-gray-900 truncate max-w-[170px] cursor-pointer text-left"
						type="button"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							handleCopyClick(row.organization.name);
						}}
					>
						{row.organization.name}
					</button>
					{row.organization.primaryDomain && (
						<span className="text-xs text-gray-500 truncate max-w-[170px]">
							{row.organization.primaryDomain}
						</span>
					)}
				</div>
			</div>
		),
	};
};

export const renderCompetitorIndustryColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.industry";

	return {
		title: ts("table.industry"),
		width: "fit-content",
		minWidth: "130px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "competitorIndustry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactCompetitorOrganization) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.organization.industry) {
						handleCopyClick(row.organization.industry);
					}
				}}
			>
				{row.organization.industry || "-"}
			</button>
		),
	};
};

export const renderCompetitorEmployeesColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.estimatedNumEmployees";

	return {
		title: ts("table.employees"),
		width: "fit-content",
		minWidth: "110px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "competitorEmployees",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactCompetitorOrganization) => (
			<button
				className="text-sm text-gray-700 cursor-pointer"
				type="button"
				onClick={(e: ReactMouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					if (row.organization.estimatedNumEmployees) {
						handleCopyClick(row.organization.estimatedNumEmployees.toLocaleString());
					}
				}}
			>
				{row.organization.estimatedNumEmployees?.toLocaleString() || "-"}
			</button>
		),
	};
};

export const renderCompetitorProspectsColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.prospects"),
		width: "fit-content",
		minWidth: "140px",
		uid: "competitorProspects",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactCompetitorOrganization) => (
			<div className="flex items-center gap-1">
				<div className="flex -space-x-2">
					{row.prospects.slice(0, 3).map((p) => (
						<div
							key={p.id}
							className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-xs font-medium text-gray-600 border-2 border-white"
							title={p.name ?? undefined}
						>
							{p.firstName?.[0]}
							{p.lastName?.[0]}
						</div>
					))}
				</div>
				{row.prospects.length > 3 && (
					<span className="text-xs text-gray-500 ml-1">+{row.prospects.length - 3}</span>
				)}
				<span className="text-xs text-gray-400 ml-1">({row.prospects.length})</span>
			</div>
		),
	};
};

export const renderCompetitorLocationColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.country";

	return {
		title: ts("table.location"),
		width: "fit-content",
		minWidth: "140px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "competitorLocation",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactCompetitorOrganization) => {
			const location =
				[row.organization.city, row.organization.country].filter(Boolean).join(", ") || "-";

			return (
				<button
					className="text-sm text-gray-700 truncate cursor-pointer"
					type="button"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						if (location !== "-") {
							handleCopyClick(location);
						}
					}}
				>
					{location}
				</button>
			);
		},
	};
};

export const renderCompetitorWebsiteColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.website"),
		width: "fit-content",
		minWidth: "90px",
		uid: "competitorWebsite",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactCompetitorOrganization) =>
			row.organization.websiteUrl ? (
				<a
					className="text-sm text-blue-action hover:underline"
					href={ensureHttpProtocol(row.organization.websiteUrl)}
					rel="noopener noreferrer"
					target="_blank"
					onClick={(e) => e.stopPropagation()}
				>
					Visit
				</a>
			) : (
				<span className="text-sm text-gray-400">-</span>
			),
	};
};

// ─── Lists columns ────────────────────────────────────────────────────────────

const ListBadges = ({ lists }: { lists?: { id: number; name: string }[] }) => {
	if (!lists || lists.length === 0) {
		return <span className="text-sm text-gray-400">-</span>;
	}

	const maxVisible = 2;
	const visible = lists.slice(0, maxVisible);
	const remaining = lists.length - maxVisible;

	const handleListClick = (e: ReactMouseEvent, listId: number) => {
		e.stopPropagation();
		window.open(`/app/lists/detail/${listId}`, "_blank");
	};

	return (
		<div className="flex flex-nowrap items-center gap-1 overflow-hidden">
			{visible.map((list) => (
				<span
					key={list.id}
					className="inline-block rounded-full bg-brand-white px-2 py-0.5 text-xs text-text-meta border border-border-light truncate max-w-[120px] cursor-pointer hover:border-blue-action hover:text-blue-action"
					title={list.name}
					onClick={(e) => handleListClick(e, list.id)}
				>
					{list.name}
				</span>
			))}
			{remaining > 0 && (
				<Tooltip
					content={
						<div className="flex flex-col gap-1">
							{lists.slice(maxVisible).map((list) => (
								<span
									key={list.id}
									className="text-sm text-text-meta cursor-pointer hover:text-blue-action"
									onClick={(e) => handleListClick(e, list.id)}
								>
									{list.name}
								</span>
							))}
						</div>
					}
					direction="bottom"
				>
					<span className="inline-block rounded-full bg-brand-white px-2 py-0.5 text-xs text-text-meta border border-border-light cursor-pointer hover:border-blue-action hover:text-blue-action whitespace-nowrap flex-shrink-0">
						+{remaining}
					</span>
				</Tooltip>
			)}
		</div>
	);
};

export const renderContactOriginListColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.originList"),
		width: "fit-content",
		minWidth: "160px",
		uid: "contactOriginList",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => {
			const origin = getOriginList(row.listPersons, row.lists);

			if (!origin) {
				return <span className="text-sm text-gray-400">-</span>;
			}

			return (
				<span
					className="inline-flex items-center rounded-full bg-brand-white px-2 py-0.5 text-xs text-text-meta border border-border-light truncate max-w-[160px] cursor-pointer hover:border-blue-action hover:text-blue-action"
					title={origin.name}
					onClick={(e: ReactMouseEvent) => {
						e.stopPropagation();
						window.open(`/app/lists/detail/${origin.id}`, "_blank");
					}}
				>
					{origin.name}
				</span>
			);
		},
	};
};

export const renderContactListsColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.lists"),
		width: "fit-content",
		minWidth: "260px",
		uid: "contactLists",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => <ListBadges lists={row.lists} />,
	};
};

export const renderOrgListsColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.lists"),
		width: "fit-content",
		minWidth: "140px",
		uid: "orgLists",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: OrganizationInfo) => <ListBadges lists={row.lists} />,
	};
};

// ─── ListPerson / Sentiment columns ─────────────────────────────────────────

const ASSIGN_STATUS_COLOR: Partial<Record<AssignStatus, string>> = {
	[AssignStatus.ASSIGNED]: "bg-status-info-light text-status-info",
	[AssignStatus.RUNNING]: "bg-status-success-light text-status-success",
	[AssignStatus.REPLIES]: "bg-status-success-light text-status-success",
	[AssignStatus.ENDED]: "bg-gray-100 text-gray-500",
	[AssignStatus.PAUSED]: "bg-status-warning-light text-status-warning",
	[AssignStatus.BLACKLIST]: "bg-status-error-light text-status-error",
	[AssignStatus.DO_NOT_CONTACT]: "bg-status-error-light text-status-error",
	[AssignStatus.DRAFT]: "bg-gray-100 text-gray-500",
	[AssignStatus.NO_REPLY]: "bg-status-warning-light text-status-warning",
	[AssignStatus.SAVED_FOR_LATER]: "bg-status-info-light text-status-info",
	[AssignStatus.MESSAGE_SENT]: "bg-status-info-light text-status-info",
	[AssignStatus.DONE]: "bg-status-success-light text-status-success",
	[AssignStatus.FOLLOW_UP]: "bg-status-info-light text-status-info",
};

function formatAssignStatus(status: AssignStatus): string {
	return status
		.replaceAll("_", " ")
		.toLowerCase()
		.replace(/\b\w/g, (c) => c.toUpperCase());
}

export const renderContactLastAssignStatusColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.lastAssignStatus"),
		width: "fit-content",
		minWidth: "140px",
		uid: "contactLastAssignStatus",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => {
			const lastLp = getLastListPerson(row.listPersons);

			if (!lastLp?.assignStatus) {
				return <span className="text-sm text-gray-400">-</span>;
			}

			const { assignStatus } = lastLp;
			const colorClass = ASSIGN_STATUS_COLOR[assignStatus] ?? "bg-gray-100 text-gray-500";

			return (
				<span
					className={classNames(
						"text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap",
						colorClass
					)}
				>
					{formatAssignStatus(assignStatus)}
				</span>
			);
		},
	};
};

const SentimentCell = ({
	sentiment,
	message,
}: {
	sentiment: ProspectInterest | null;
	message: string | null;
}) => {
	if (!sentiment) {
		return <span className="text-sm text-gray-400">-</span>;
	}

	const label = formatSentimentLabel(sentiment);
	const colorClass = getSentimentColor(sentiment);
	const tooltipContent = message ? convertHtmlToLinkedInText(message) : undefined;

	const badge = (
		<span
			className={classNames(
				"text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap cursor-default",
				colorClass
			)}
		>
			{label}
		</span>
	);

	if (tooltipContent) {
		return <Tooltip content={tooltipContent}>{badge}</Tooltip>;
	}

	return badge;
};

export const renderContactLastSentimentColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.lastSentiment"),
		width: "fit-content",
		minWidth: "140px",
		uid: "contactLastSentiment",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => {
			const conv = row.lastSentimentConversation;

			return (
				<SentimentCell
					message={conv?.message ?? null}
					sentiment={(conv?.sentiment as ProspectInterest) ?? null}
				/>
			);
		},
	};
};

export const renderContactFirstSentimentColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.firstSentiment"),
		width: "fit-content",
		minWidth: "140px",
		uid: "contactFirstSentiment",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => {
			const conv = row.firstSentimentConversation;

			return (
				<SentimentCell
					message={conv?.message ?? null}
					sentiment={(conv?.sentiment as ProspectInterest) ?? null}
				/>
			);
		},
	};
};

export const renderContactBestSentimentColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.bestSentiment"),
		width: "fit-content",
		minWidth: "140px",
		uid: "contactBestSentiment",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => {
			const conv = row.bestSentimentConversation;

			return (
				<SentimentCell
					message={conv?.message ?? null}
					sentiment={(conv?.sentiment as ProspectInterest) ?? null}
				/>
			);
		},
	};
};

// ─── Date columns ─────────────────────────────────────────────────────────────

const formatDateCell = (value: string | null | undefined) => {
	if (!value) {
		return <span className="text-sm text-gray-400">-</span>;
	}

	const date = new Date(value);

	return <span className="text-sm text-text">{format(date, "dd.MM.yyyy")}</span>; // date.toLocaleDateString()}</span>;
};

export const renderContactLastContactAtColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.lastContactAt";

	return {
		title: ts("table.lastContactAt"),
		width: "fit-content",
		minWidth: "140px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactLastContactAt",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => formatDateCell(row.lastContactAt),
	};
};

export const renderContactLastResponseAtColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.lastResponseAt";

	return {
		title: ts("table.lastResponseAt"),
		width: "fit-content",
		minWidth: "140px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactLastResponseAt",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => formatDateCell(row.lastResponseAt),
	};
};

// ─── Profile Tracker column ──────────────────────────────────────────────────

const TrackerBadge = ({ name, link }: { name: string; link?: string }) => {
	const badge = (
		<span
			className="inline-block rounded-full bg-status-success-light px-2 py-0.5 text-xs text-status-success truncate max-w-[120px]"
			title={name}
		>
			{name}
		</span>
	);

	if (link) {
		return (
			<a className="hover:opacity-80" href={link} onClick={(e) => e.stopPropagation()}>
				{badge}
			</a>
		);
	}

	return badge;
};

export const renderContactProfileTrackerColumn = (
	ts: (key: string) => string,
	onAddToTracker: (prospectId: number) => void
): TableColumn => {
	return {
		title: ts("table.profileTracker"),
		width: "fit-content",
		minWidth: "160px",
		uid: "contactProfileTracker",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ContactProspect) => {
			// Collect tracker names from both lists (sourceType=TRACKER) and trackerPersons
			const trackerEntries: { key: string; name: string; link: string }[] = [];

			const trackerLists = row.lists?.filter((list) => {
				if (list.sourceType === ListSourceType.TRACKER) {
					return row.trackerPersons?.some((tp) => tp.tracker?.list?.id === list.id);
				}

				if (list.sourceType === ListSourceType.JOB_CHANGE) {
					const listPerson = row.listPersons?.find((lp) => lp.listId === list.id);

					return listPerson?.assignStatus === AssignStatus.UNDER_REVIEW;
				}

				return false;
			});

			if (trackerLists) {
				for (const list of trackerLists) {
					const searchTerm = `${
						list.sourceType === ListSourceType.JOB_CHANGE ? "&searchTerm=" : "&tracker_search="
					}${encodeURIComponent(
						list.sourceType === ListSourceType.JOB_CHANGE
							? row.name || ""
							: row.organization?.name || ""
					)}`;

					const tab =
						list.sourceType === ListSourceType.JOB_CHANGE ? "UNDER_REVIEW" : "TRACKED_PROFILE";

					trackerEntries.push({
						key: `list-${list.id}`,
						name: list.name,
						link: `/app/lists/detail/${list.id}?tab=${tab}${searchTerm}`,
					});
				}
			}

			if (row.trackerPersons) {
				for (const tp of row.trackerPersons) {
					const trackerListId = tp.tracker?.list?.id;

					if (trackerListId === undefined || trackerListId === null) {
						continue;
					}

					const alreadyShown = trackerLists?.some((l) => l.id === trackerListId);

					if (!alreadyShown) {
						trackerEntries.push({
							key: `tp-${tp.id}`,
							name: tp.tracker?.list?.name ?? tp.tracker.name,
							link: `/app/lists/detail/${trackerListId}?tab=TRACKED_PROFILE`,
						});
					}
				}
			}

			if (trackerEntries.length > 0) {
				const maxVisible = 2;
				const visible = trackerEntries.slice(0, maxVisible);
				const remaining = trackerEntries.length - maxVisible;

				return (
					<div className="flex flex-nowrap items-center gap-1 overflow-hidden">
						{visible.map((entry) => (
							<TrackerBadge key={entry.key} link={entry.link} name={entry.name} />
						))}
						{remaining > 0 && (
							<Tooltip
								content={
									<div className="flex flex-col gap-1">
										{trackerEntries.slice(maxVisible).map((entry) => (
											<a
												key={entry.key}
												className="text-xs hover:underline"
												href={entry.link}
												onClick={(e) => e.stopPropagation()}
											>
												{entry.name}
											</a>
										))}
									</div>
								}
							>
								<span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 cursor-default whitespace-nowrap flex-shrink-0">
									+{remaining}
								</span>
							</Tooltip>
						)}
					</div>
				);
			}

			return (
				<Button
					color={ButtonColor.ACTION}
					image={<Icon className="w-3 mr-1" icon={faPlus} />}
					size={ButtonSize.XS}
					title={ts("addToTracker")}
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onAddToTracker(row.id);
					}}
				/>
			);
		},
	};
};

export const renderOrgProfileTrackerColumn = (
	ts: (key: string) => string,
	onAddToTracker: (organizationId: number) => void
): TableColumn => {
	return {
		title: ts("table.profileTracker"),
		width: "fit-content",
		minWidth: "160px",
		uid: "orgProfileTracker",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: OrganizationInfo) => {
			const trackerLists = row.trackerLists;

			if (trackerLists && trackerLists.length > 0) {
				const maxVisible = 2;
				const visible = trackerLists.slice(0, maxVisible);
				const remaining = trackerLists.length - maxVisible;

				return (
					<div className="flex flex-nowrap items-center gap-1 overflow-hidden">
						{visible.map((tl) => (
							<TrackerBadge
								key={tl.listId}
								link={`/app/lists/detail/${tl.listId}?tab=TRACKED_PROFILE&tracker_search=${encodeURIComponent(
									row.name || ""
								)}`}
								name={tl.listName}
							/>
						))}
						{remaining > 0 && (
							<Tooltip
								content={
									<div className="flex flex-col gap-1">
										{trackerLists.slice(maxVisible).map((tl) => (
											<a
												key={tl.listId}
												className="text-xs hover:underline"
												href={`/app/lists/detail/${tl.listId}?tab=TRACKED_PROFILE`}
												onClick={(e) => e.stopPropagation()}
											>
												{tl.listName}
											</a>
										))}
									</div>
								}
							>
								<span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 cursor-default whitespace-nowrap flex-shrink-0">
									+{remaining}
								</span>
							</Tooltip>
						)}
					</div>
				);
			}

			return (
				<Button
					color={ButtonColor.ACTION}
					image={<Icon className="w-3 mr-1" icon={faPlus} />}
					size={ButtonSize.XS}
					title={ts("addToTracker")}
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onAddToTracker(row.id);
					}}
				/>
			);
		},
	};
};

// Company-fact columns live in ./columns/contact-company-facts-columns.tsx; re-exported here
// because the contacts table builds its column list from this module.
export {
	renderContactCompanyFoundedYearColumn,
	renderContactCompanyIndustryColumn,
	renderContactCompanySizeColumn,
	renderContactNormalizedCompanyNameColumn,
};
